import { HTMLAttributes } from 'react';
import { usePopperTooltip } from 'react-popper-tooltip';
import { NavLink } from 'react-router-dom';

import { numberCap } from 'helpers';
import { MenuNavItem } from 'types';

interface Props extends HTMLAttributes<HTMLElement> {
  itemProps: MenuNavItem;
}

export function NavItemHover({ itemProps }: Props) {
  const { getTooltipProps, setTooltipRef, setTriggerRef, visible } =
    usePopperTooltip({
      trigger: 'hover',
      offset: [0, 0],
      interactive: true,
      placement: 'right-start',
    });

  const { icon, title, itemList } = itemProps;

  const generalNotification = itemList.some(({ notification }) => notification);
  const generalNumericalIndicator = itemList.reduce(
    (acc, { numericalIndicator }) => {
      if (numericalIndicator && numericalIndicator > acc)
        acc = acc + numericalIndicator;

      return acc;
    },
    0
  );

  const hasNotification =
    generalNotification || Boolean(generalNumericalIndicator);

  const hasActiveChild = itemList.some(
    ({ path }) => window.location.pathname === path
  );
  const hasSingleChild = itemList.length === 1;

  const getMenuItemClassName = (isActive: boolean) =>
    `border-solid border-l-[6px] ${
      isActive
        ? 'bg-neutral-500 border-primary-300'
        : 'bg-neutral-700 hover:bg-neutral-500 hover:border-neutral-500 border-neutral-700'
    }`;

  const renderNotification = () => (
    <span
      className="absolute w-6 h-6 rounded-full top-0.5 right-0.5 bg-primary-300 font-bold text-xs
        flex items-center justify-center"
    >
      {Boolean(generalNumericalIndicator)
        ? numberCap(generalNumericalIndicator, 99)
        : '!'}
    </span>
  );

  if (hasSingleChild) {
    const singleChildClassName = ({ isActive }: { isActive: boolean }) =>
      `group relative flex items-center justify-center size-16
      ${getMenuItemClassName(isActive)}`;

    return (
      <NavLink to={itemList[0].path} className={singleChildClassName}>
        {hasNotification && renderNotification()}
        {icon}
      </NavLink>
    );
  }

  const triggerClassName = `relative flex items-center justify-center size-16
    ${getMenuItemClassName(hasActiveChild)}`;

  const subItemClassName = ({ isActive }: { isActive: boolean }) =>
    `flex items-center justify-between text-neutral-100 text-sm px-4 py-3
    cursor-pointer no-underline relative ${getMenuItemClassName(isActive)}`;

  return (
    <div className="group">
      <div ref={setTriggerRef} className={triggerClassName}>
        {hasNotification && renderNotification()}
        {icon}
      </div>

      {visible && (
        <div
          ref={setTooltipRef}
          {...getTooltipProps()}
          className="overflow-hidden rounded-r"
        >
          <div className="flex flex-col bg-neutral-700 w-72">
            <span className="text-base font-bold px-4 py-3 border-b border-solid border-b-neutral-400/50">
              {title}
            </span>

            {itemList.map(
              ({ name, path, notification, numericalIndicator }) => {
                const hasItemNotification =
                  notification || Boolean(numericalIndicator);

                return (
                  <NavLink to={path} key={name} className={subItemClassName}>
                    {name}

                    {hasItemNotification && (
                      <span
                        className="min-w-6 h-6 rounded-full bg-primary-300 font-bold text-xs flex items-center
                          justify-center"
                      >
                        {Boolean(numericalIndicator) ? (
                          numberCap(numericalIndicator || 0, 99)
                        ) : (
                          <b className="px-2.5 font-bold">Novo!</b>
                        )}
                      </span>
                    )}
                  </NavLink>
                );
              }
            )}
          </div>
        </div>
      )}
    </div>
  );
}
